import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * CascadeDeletePrompt - Generic modal for handling deletions with dependencies
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - title: string
 * - message: string
 * - affectedItems: Array<{ name: string, description?: string }>
 * - actions: Array<{ label: string, onClick: () => void, variant: 'danger' | 'warning' | 'primary' | 'secondary' }>
 * - isProcessing: boolean
 */
const CascadeDeletePrompt = ({
  isOpen,
  onClose,
  title = 'Confirm Action',
  message,
  affectedItems = [],
  actions = [],
  isProcessing = false,
}) => {
  if (!isOpen) return null;

  const getButtonClasses = (variant) => {
    switch (variant) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'warning':
        return 'bg-yellow-500 hover:bg-yellow-600 text-white';
      case 'primary':
        return 'bg-purple-600 hover:bg-purple-700 text-white';
      case 'secondary':
      default:
        return 'bg-gray-100 hover:bg-gray-200 text-gray-700';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message */}
        {message && (
          <p className="text-gray-600 mb-4">{message}</p>
        )}

        {/* Affected Items */}
        {affectedItems.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Affected items:</p>
            <ul className="bg-gray-50 rounded-lg p-3 space-y-2">
              {affectedItems.map((item, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  <span className="font-medium text-gray-800">{item.name}</span>
                  {item.description && (
                    <span className="text-gray-500">- {item.description}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={isProcessing}
              className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${getButtonClasses(action.variant)}`}
            >
              {isProcessing ? 'Processing...' : action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CascadeDeletePrompt;